import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { colors, fontWeights, breakpoints } from '@/styles'
import { StatusContainer } from '@/features/Application/elements'
import Skeleton from '@/components/skeleton'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 18px;
`

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-bottom: 1px solid ${colors.grey88};
  padding: 12px 12px;
  font-weight: ${fontWeights.medium};
  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    background: ${colors.grey96};
  }
`

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const Text = styled.span`
  font-weight: ${fontWeights.regular};
  font-size: 14px;
  line-height: 20px;
  color: ${colors.grey16};
  margin: 0;
  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`
const BoldText = styled(Text)`
  font-weight: ${fontWeights.medium};
`

const StatusText = styled.span`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 20px;
  color: ${props => {
    switch (props.status) {
      case 'records_found':
        return colors.red40
      case 'records_not_found':
        return colors.green48
      default:
        return colors.grey16
    }
  }};
  margin: 0;
  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`

const SubText = styled.p`
  font-weight: ${fontWeights.regular};
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey16};
  margin: 12px 0 0 0;
  ${props =>
    props.isLocked &&
    `
    filter: blur(10px);
    color: ${colors.black};
    background-color: ${colors.grey96};
    -webkit-touch-callout: none;
    -webkit-user-select: none; 
    -khtml-user-select: none; 
    -moz-user-select: none; 
    -ms-user-select: none; 
    user-select: none; 
    cursor: not-allowed;
  `}
`

const Mobile = ({ data = {}, isLocked, application, isLoading }) => {
  const { t } = useTranslation()
  const { court_verification } = data ?? {}
  const title = t('l.application.view.court_record_verification.label', 'Court Record Verification')
  const showStatus = true
  const rates = {
    records_found: 'poor',
    records_not_found: 'excellent',
    analyzing: 'analyzing'
  }
  const statusType = rates[court_verification]
  const status = {
    records_found: t('l.application.view.poor.label', 'Poor'),
    records_not_found: t('l.application.view.excellent.label', 'Excellent'),
    analyzing: t('l.application.view.analyzing.label', 'Analyzing')
  }

  const statusText = status[court_verification]

  const texts = {
    records_found: t('l.application.view.records_found.label', 'records found'),
    records_not_found: t('l.application.view.no_records.label', 'no records'),
    analyzing: t('l.application.view.analyzing_lowercase.text', 'analyzing')
  }
  const text = texts[court_verification]
  const randomWords = '***** ******** *****'
  return (
    <Wrapper>
      <StatusContainer
        title={title}
        showStatus={showStatus}
        statusType={statusType}
        statusTitle={statusText}
        isLocked={isLocked}
        isLoading={isLoading}
      >
        <Container>
          {isLoading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <EmptyWrapper>
              <Text isLocked={isLocked}>
                {isLocked ? (
                  randomWords
                ) : court_verification === 'analyzing' ? (
                  <Trans
                    i18nKey="l.application.view.court_record_verification_analyzing.text"
                    t={t}
                  >
                    Our team is still <BoldText>analyzing</BoldText> this applicant's records.
                    Please check back in a few hours. If records of possible infractions are
                    detected on public provincial/state court records, the results will be indicated
                    here.
                  </Trans>
                ) : (
                  <Trans
                    i18nKey="l.application.view.court_record_verification.text"
                    t={t}
                    text={text}
                  >
                    This applicant has
                    <StatusText
                      statusTitle={statusText}
                      status={court_verification}
                      isLocked={isLocked}
                    >
                      {{ text }}
                    </StatusText>
                    found of possible infractions detected on public provincial/state court records.
                  </Trans>
                )}
              </Text>
              <SubText isLocked={isLocked}>
                {t(
                  'l.application.view.all_records.label',
                  'All records are gathered from a third-party source. liv.rent bears no responsibility for potential inaccuracies that may arise'
                )}
              </SubText>
            </EmptyWrapper>
          )}
        </Container>
      </StatusContainer>
    </Wrapper>
  )
}

export default Mobile
